import React, {useContext, useEffect, useRef, useState} from 'react';
import Icon from '../Icon/Icon';
import {
  apiBaseUrl,
  axiosInstance,
  esputnikTracker,
  eventEmitter, hoverBtnClass,
  lang, sessid
} from '../../common/baseData';
import SearchResult from './SearchResult';
import {
  ClientWidthContext,
  ModalContext, ModalDispatchContext, SearchContext, SearchDispatchContext,
} from '../Context/ApplicationContextProvider';
import {DebounceInput} from 'react-debounce-input';
import Lang from '../Lang/Lang';
import Cookies from 'js-cookie';
import {useMobileSearchActions} from './MobileSearchContext';

function SearchForm(props) {
  const {children, defineInputFocus, type} = {...props};
  const {
    query,
    start,
    direct,
    productList,
    categoryList,
    historyList,
    promotedProducts,
    potentialQueryList,
    total,
    totalLink,
  } = useContext(SearchContext);
  const dispatchSearch = useContext(SearchDispatchContext);
  const inputRef = useRef(null);
  const mobileInputValueRef = useRef(query);
  const mobileDebounceRef = useRef(null);
  const ref = useRef(null);
  const modals = useContext(ModalContext);
  const dispatchModal = useContext(ModalDispatchContext);
  const clientWidth = useContext(ClientWidthContext);
  const isMobile = clientWidth < 1280;
  const isMobileSearch = type === 'mobileSearch';
  const mobileSearchActions = useMobileSearchActions();
  const searchPage = `${lang === 'ru' ? '/' : `/${lang}/`}search/`;

  const [inputValue, setInputValue] = useState(query);

  // Получаем поисковый запрос при монтировании компонента
  useEffect(() => {
    if (window.searchPageData) {
      try {
        let actualSearch = JSON.parse(atob(window.searchPageData));
        if (actualSearch.query) {
          setInputValue(actualSearch.query);
          mobileInputValueRef.current = actualSearch.query;
          dispatchSearch({type: 'setQuery', payload: actualSearch.query});
        }
      } catch (error) {
        console.error('Ошибка при парсинге searchPageData:', error);
      }
    }
  }, []);

  useEffect(() => {
    if (!isMobileSearch || !(inputRef.current instanceof HTMLInputElement)) {
      return;
    }

    mobileInputValueRef.current = query;
    inputRef.current.value = query;
  }, [isMobileSearch, query]);

  useEffect(() => () => {
    if (mobileDebounceRef.current !== null) {
      clearTimeout(mobileDebounceRef.current);
    }
  }, []);

  /** Принудительная установка фокуса при определенном условии **/
  useEffect(() => {
    if (defineInputFocus && inputRef.current instanceof HTMLElement) {
      inputRef.current.focus();
    }
  }, [inputRef.current, defineInputFocus]);

  /**
   * событие смены прискового запроса
   */
  useEffect(() => {
    if (query.length <= 0) return;
    // сохраняем последнюю поисковую фразу
    Cookies.set('_ms_search_phase', query, {expires: 1});
  }, [query]);

  /**
   * событие клика / закрытие поиска
   */
  useEffect(() => {
    document.addEventListener('click', handleClickOutside, true);
    return () => {
      document.removeEventListener('click', handleClickOutside, true);
    };
  }, []);

  /**
   * событие клика вне блока
   */
  function handleClickOutside(event) {
    if (clientWidth > 1279 && ref.current &&
      !ref.current.contains(event.target)) {
      setSearchActive(false);
    }
  }

  const defaultClass = 'desktop:mx-12';
  const activeClass =  'desktop:ml-12';

  function onKeyUp(e) {
    const currentValue = isMobileSearch
      ? mobileInputValueRef.current
      : inputValue;
    if (e.code === 'Enter' && currentValue.length) onSubmitQuery();
  }

  function onInputHandler(e) {
    setInputValue(e.target.value);
  }

  function onChangeHandler(e) {
    const newValue = e.target.value;
    setInputValue(newValue);
  }

  function onMobileInput(e) {
    const newValue = e.currentTarget.value;
    mobileInputValueRef.current = newValue;

    if (mobileDebounceRef.current !== null) {
      clearTimeout(mobileDebounceRef.current);
    }

    mobileDebounceRef.current = setTimeout(() => {
      dispatchSearch({type: 'setQuery', payload: newValue});
      mobileDebounceRef.current = null;
    }, 500);
  }

  function onSubmitQuery() {
    if (!!direct && direct.length) {
      if (direct.startsWith('https://')) {
        window.location.href = direct;
      } else {
        window.location.href = lang === 'ua' ?
            `/${lang}${direct}` :
            direct;
      }
    } else {
      // Определяем URL для поиска
      const targetUrl = total ? totalLink.split('?')[0] : searchPage;
      const searchQuery = total
        ? totalLink.split('q=')[1]
        : isMobileSearch ? mobileInputValueRef.current : inputValue;

      // Создаем временную форму для POST запроса
      const form = document.createElement('form');
      form.method = 'POST';
      form.action = targetUrl + '?q=' + searchQuery;
      form.style.display = 'none';

      // Добавляем параметр поиска
      const input = document.createElement('input');
      input.type = 'hidden';
      input.name = 'q';
      input.value = searchQuery;

      // Добавляем параметр сессии
      const inputSessid = document.createElement('input');
      inputSessid.type = 'hidden';
      inputSessid.name = 'sessid';
      inputSessid.value = sessid;

      form.appendChild(input);
      form.appendChild(inputSessid);
      document.body.appendChild(form);
      form.submit();
      document.body.removeChild(form);
    }
  }


  /**
   * установка активности поиска
   * @param active
   */
  function setSearchActive(active) {
    if (isMobileSearch) {
      if (active) {
        mobileSearchActions.open();
      } else {
        if (mobileDebounceRef.current !== null) {
          clearTimeout(mobileDebounceRef.current);
          mobileDebounceRef.current = null;
        }
        mobileSearchActions.close();
      }
      return;
    }

    dispatchModal({type: active ? 'show' : 'hide', modalType: type});
    if (!active) dispatchSearch({type: 'clear'});
  }

  return (
    <div className={`relative w-full desktop:w-auto flex flex-col h-[35px] desktop:h-[46px] grow ${modals.includes('search') ? activeClass : defaultClass}`} ref={ref}>
      <form name="site-search" className={'w-full flex h-full'}
            onSubmit={(e) => {
              e.preventDefault();
              onSubmitQuery();
            }}
            data-testid="search-form">
        <span className="absolute left-3 flex justify-center items-center h-full">
          <Icon name={'search-icon'} className="h-4 w-4"/>
        </span>
        {
          isMobileSearch ? (
            <input
              ref={inputRef}
              defaultValue={query}
              enterKeyHint="search"
              type="text"
              autoComplete="off"
              placeholder={Lang.getMessage('search')}
              className={`w-full p-3 px-10 h-full flex gap-3 rounded-[14px] border-grey-20 border border-solid text-[14px] leading-[21px] text-grey-90`}
              id="h-mobile-search-input"
              onInput={onMobileInput}
              onFocus={event => event.stopPropagation()}
              onKeyUp={onKeyUp}/>
          ) : (
            <DebounceInput
              value={query}
              inputRef={inputRef}
              enterKeyHint="search"
              minLength={2}
              debounceTimeout={500}
              type="text"
              autoComplete="off"
              placeholder={Lang.getMessage('search')}
              className={`w-full p-3 ${isMobile ? 'px-10' : 'pl-10'} h-full flex gap-3 rounded-[14px] border-grey-20 border border-solid text-[14px] leading-[21px] text-grey-90 desktop:border-r-0 desktop:rounded-l-[14px] desktop:rounded-tr-none desktop:rounded-br-none`}
              id="h-search-input"
              onChange={onChangeHandler}
              onInput={(e) => {
                onInputHandler(e);
                dispatchSearch({type: 'setQuery', payload: e.target.value});
              }}
              onFocus={event => {
                event.stopPropagation();
                setSearchActive(true);
              }}
              onKeyUp={onKeyUp}/>
          )
        }
        <button type={'button'} onClick={onSubmitQuery}
                className={`hidden desktop:block w-[115px] bg-blue-primary text-white text-[16px] leading-[24px] border-0 rounded-r-[14px] h-full ${hoverBtnClass}`}>
          {Lang.getMessage('search')}
        </button>
        {
          (isMobileSearch || modals.includes('search')) &&
          isMobile && (
            <span
              className={'absolute cursor-pointer right-[-2px] top-0 h-full w-10 flex justify-center items-center'}
              onClick={() => {
                setSearchActive(false);
              }}>
              <Icon name={'close-icon'} className="h-4 w-4"/>
            </span>
          )
        }
      </form>
      {children}
    </div>
  );
}

export default SearchForm;